import { useState } from "react"

import CloseIcon from "@mui/icons-material/Close"
import {
  Stack,
  Typography,
  Drawer,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material"
import { useTranslation } from "react-i18next"

import { DateSelector, LinkButtonHybrid } from "@/components"
import { useDateParam, useResortParam } from "@/hooks/useSkipassFlow"
import { useSalePeriodQuery } from "@/queries/hooks"

import { PriceAlert } from "./components"
import PriceSummaryView from "./PriceSummaryView"

const PriceSummaryDrawer = () => {
  const { startDate } = useDateParam()
  const { resortExternalId } = useResortParam()

  const [open, setOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date>(
    startDate || new Date()
  )
  const [selectedPassTypeId, setSelectedPassTypeId] = useState<string>("")
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const { data: salePeriod, isLoading: isSalePeriodLoading } =
    useSalePeriodQuery(resortExternalId)

  const onClose = () => {
    setOpen(false)
  }

  const handleDateChange = (date: Date) => {
    setSelectedPassTypeId("")
    setSelectedDate(date)
  }

  return (
    <>
      <LinkButtonHybrid
        onClick={() => {
          setOpen(true)
        }}
      >
        {t("priceSummary")}
      </LinkButtonHybrid>
      <Drawer
        open={open}
        onClose={onClose}
        anchor={isMobile ? "bottom" : "right"}
        slotProps={{
          paper: {
            sx: {
              width: { xs: "100%", sm: "512px", md: "704px" },
              height: { xs: "90vh", sm: "100%" },
              borderTopLeftRadius: isMobile ? 4 : 0,
              borderTopRightRadius: isMobile ? 4 : 0,
              display: "flex",
              flexDirection: "column",
            },
          },
        }}
      >
        <Stack height="100%" direction="column">
          <Stack p={2}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h7" component="h2">
                {t("priceSummary")}
              </Typography>
              <IconButton
                onClick={onClose}
                aria-label="Close"
                sx={{ color: "text.primary", p: 0 }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
          <Divider />

          <Stack flex={1} overflow="auto" spacing={1} p={2}>
            <PriceAlert />
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t("priceSummaryView.validityDate", {
                date: new Date().toISOString().split("T")[0],
              })}
            </Typography>

            <DateSelector
              label={t("priceSummaryView.dateSelectLabel")}
              selectedDate={selectedDate}
              setSelectedDate={handleDateChange}
              minDate={salePeriod?.startDate}
              maxDate={salePeriod?.endDate}
              unAvailableDates={salePeriod?.unAvailableDates ?? []}
              isLoadingDates={isSalePeriodLoading}
              maxWidth="357px"
            />

            <PriceSummaryView
              selectedDate={selectedDate}
              selectedPassTypeId={selectedPassTypeId}
              setSelectedPassTypeId={setSelectedPassTypeId}
            />
          </Stack>
          <Divider />
          <Stack
            direction="row"
            spacing={2}
            padding={2}
            justifyContent="flex-end"
          >
            <Button type="button" variant="contained" onClick={onClose}>
              {t("priceSummaryView.closeButtonLabel")}
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    </>
  )
}

export default PriceSummaryDrawer
